/**
 * Owns the DEV-ONLY per-station WebGL fusion research mode. Demoted from the
 * default composite view (workers/CONTRACTS.md "Mosaic-head": the server-
 * baked CONUS mosaic, mosaicController.ts, is the mass-audience default now
 * — mosaic pixels are byte-identical to station pixels by construction, so
 * no fidelity is lost by making it the default at every zoom); this remains
 * the pro/research view for overlap and blend-rule work, toggled via app.ts's
 * render-mode picker (compositeRenderMode === "fusion", config.ts's
 * getCompositeRenderMode, dev-hosts only).
 *
 * Owns fleet.json discovery (with graceful fallback to the per-station
 * manifests app.ts already polls for loop/scrub history), per-station COG
 * decode at a zoom-chosen overview level (fusionDecoder.ts/fusionGeo.ts —
 * fusionGeo.ts's overviewLevelForZoom and fusionDecoder.ts's off-main-thread
 * decode are reused VERBATIM by mosaicController.ts too, since the mosaic's
 * z7 grid is the identical lattice every station bakes onto), the WebGL
 * blend itself (fusionCompositor.ts), a two-compositor crossfade so redraws
 * never hard-cut, preloading, and dev-mode perf instrumentation. There's no
 * separate "wide" vs "regional" mode to hand off between: fusion renders at
 * every zoom in the wide view, converging toward one dominant station's own
 * detail as you zoom into its footprint.
 *
 * Active/inactive is driven by TWO things (not just station drill-down
 * anymore): app.ts's viewMode (station drill-down) AND compositeRenderMode
 * (the render-mode picker) — see app.ts's activateCompositeRenderMode().
 */
import type * as maplibregl from "maplibre-gl";
import { frameUrl, fleetManifestUrl } from "./config";
import { ManifestPoller } from "./manifestPoller";
import type { FleetManifest, FleetStation } from "./fleetTypes";
import type { HotManifest } from "./types";
import { STATIONS } from "./stations";
import { lonLatToMercator } from "./geo";
import { overviewLevelForZoom, lonLatBoundsToMercator, stationUvTransform, boundsIntersect, type Bounds3857, type OverviewLevel } from "./fusionGeo";
import { FusionCompositor, type BlendMode, BLEND_MODES, PRECIP_FLOOR_RAW, NO_FLOOR_RAW } from "./fusionCompositor";
import { FusionDecoder, type StationDecodeResult } from "./fusionDecoder";
import { LruCache } from "./frameCache";
import { frameAtOrBefore } from "./scrub";

export { BLEND_MODES, type BlendMode };

const SOURCE_IDS = ["radar-fusion-a", "radar-fusion-b"] as const;
const layerIdFor = (sourceId: string): string => `${sourceId}-layer`;

const FUSION_OPACITY = 0.85;
/** Same crossfade duration/rationale as the (removed) mosaic path: driven
 * by MapLibre's own raster-opacity-transition, not a hand-rolled RAF loop.
 * Exported so app.ts's legacy fallback composite layers (used only when
 * fusionAvailable is false) fade at the same rate instead of a second
 * hand-picked constant drifting out of sync with this one. */
export const CROSSFADE_MS = 150;
const PRELOAD_AHEAD = 2;
const POLL_INTERVAL_MS = 10_000;
const CACHE_CAP = 64;
/** Skip fetching a station whose center sits well outside the current
 * viewport — pure fetch-avoidance (correctness doesn't need it: the
 * shader's own per-pixel bounds check already excludes non-covering
 * stations from the blend). ~2.5deg ≈ a station's own ~230km nominal range
 * at these latitudes, so a station just outside the visible box but still
 * within range of its edge isn't dropped. */
const RELEVANCE_PAD_DEG = 2.5;
/** Output canvas resolution cap — bounds GPU/memory cost on very large or
 * high-DPI displays; the map's own devicePixelRatio-scaled canvas size is
 * used below that. */
const MAX_OUTPUT_PX = 2048;

const TRANSPARENT_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=";
const PLACEHOLDER_COORDS: [[number, number], [number, number], [number, number], [number, number]] = [
  [-1, 1],
  [1, 1],
  [1, -1],
  [-1, -1],
];

const DEV = import.meta.env.DEV;

export class FusionController {
  private poller: ManifestPoller<FleetManifest>;
  private decoder = new FusionDecoder();
  private cache = new LruCache<StationDecodeResult>(CACHE_CAP);

  private compositors: [FusionCompositor | null, FusionCompositor | null] = [null, null];
  /** True once construction of both compositors succeeded — false means no
   * usable WebGL context; app.ts falls back to the legacy per-station
   * painter's-order composite layers it keeps around for exactly this.
   * Meaningful only once `initialized` is true — see init()'s doc comment. */
  fusionAvailable = false;
  /** True once init() has run to completion (success or failure) — app.ts
   * must check this before trusting `fusionAvailable`'s value at all. */
  initialized = false;
  private activeSlot: 0 | 1 = 0;
  private layersReady = false;

  private active = false;
  private liveMode = true;
  private lastScrubMs: number | null = null;
  private blendMode: BlendMode = "owner-fallback";
  /** Fused-view display floor (app.ts's HUD toggle) — true (default) hides
   * sub-10 dBZ returns ("precip only"); false shows everything a station
   * decoded ("all returns"). Station drill-down is unaffected by this —
   * it's a fused-canvas-only concern, always the honest raw view. */
  private floorEnabled = true;

  private fleetAvailable = false;
  private fleetStations: FleetStation[] | null = null;
  private compositeManifests: ReadonlyMap<string, HotManifest | null> = new Map();

  /** Per-STATIONS-index (fixed slot, not reassigned): what's currently
   * loaded into the compositors for that station, if anything. */
  private slotTs: (string | null)[] = STATIONS.map(() => null);
  private slotLevel: (OverviewLevel | null)[] = STATIONS.map(() => null);
  private slotBbox: (Bounds3857 | null)[] = STATIONS.map(() => null);
  private readonly stationCenters: [number, number][] = STATIONS.map((s) => lonLatToMercator(s.lon, s.lat));

  private renderScheduled = false;
  private firstPaintLogged = false;

  constructor(
    private map: maplibregl.Map,
    private getBaseUrl: () => string,
    private onDataChanged: () => void,
  ) {
    this.poller = new ManifestPoller<FleetManifest>(
      () => fleetManifestUrl(this.getBaseUrl()),
      (m) => {
        this.fleetAvailable = true;
        this.fleetStations = m.stations;
        this.onStationsChanged();
      },
      () => {
        // fleet.json absent or erroring — CONTRACTS.md: "it may deploy after
        // you." Fall back to whatever app.ts's per-station polling already
        // knows (onCompositeManifestsChanged, below); keep retrying fleet on
        // its normal cadence so it self-heals the moment it does exist.
        this.fleetAvailable = false;
        this.onStationsChanged();
      },
      POLL_INTERVAL_MS,
    );
  }

  /** Call once, after the map's "load" event. Tries to stand up two WebGL
   * compositors; on failure (no usable GL context anywhere), leaves
   * `fusionAvailable` false and adds no layers — app.ts checks this and
   * uses the legacy per-station layers instead.
   *
   * `initialized` (separate from `fusionAvailable`) exists because manifest
   * polling starts in app.ts's constructor, synchronously, well before
   * "load" fires — a manifest can resolve and call
   * onCompositeManifestsChanged() before this method has ever run. app.ts's
   * legacy-fallback decision checks `initialized` first: `fusionAvailable`'s
   * pre-init default (false) must never be read as "confirmed unavailable,
   * use the fallback" — that was a real bug (verified empirically: KAMX's
   * manifest routinely resolves fast enough on localhost to win this race,
   * triggering a spurious full-file fetch via the legacy path on cold load
   * even though fusion was about to be available all along). */
  init(): void {
    if (this.initialized) return;
    try {
      this.compositors = [new FusionCompositor(STATIONS.length), new FusionCompositor(STATIONS.length)];
      this.fusionAvailable = true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[fusion] WebGL unavailable, falling back to per-station layers: ${(err as Error).message}`);
      this.fusionAvailable = false;
      this.initialized = true;
      return;
    }

    for (const sourceId of SOURCE_IDS) {
      this.map.addSource(sourceId, { type: "image", url: TRANSPARENT_PIXEL, coordinates: PLACEHOLDER_COORDS });
      const layerId = layerIdFor(sourceId);
      this.map.addLayer({
        id: layerId,
        type: "raster",
        source: sourceId,
        paint: { "raster-opacity": 0, "raster-resampling": "linear" },
      });
      this.map.setPaintProperty(layerId, "raster-opacity-transition", { duration: CROSSFADE_MS });
    }
    this.layersReady = true;
    this.initialized = true;

    this.map.on("moveend", () => this.onViewportSettled());
    this.map.on("zoomend", () => this.onViewportSettled());
  }

  start(): void {
    this.poller.start();
  }

  stop(): void {
    this.poller.stop();
  }

  setActive(active: boolean): void {
    if (!this.fusionAvailable || this.active === active) return;
    this.active = active;
    const layerId = layerIdFor(SOURCE_IDS[this.activeSlot]);
    if (this.map.getLayer(layerId)) {
      this.map.setPaintProperty(layerId, "raster-opacity", active ? FUSION_OPACITY : 0);
    }
    if (!active) return;
    // Catch up on data (whichever transport mode is current — onStationsChanged
    // itself no-ops on non-live, so scrub mode needs its own explicit re-run
    // against the remembered target rather than whatever ts was loaded before
    // this went hidden) and the window (viewport may differ from before too).
    if (this.liveMode) this.onStationsChanged();
    else if (this.lastScrubMs !== null) this.showAtOrBefore(this.lastScrubMs);
    this.scheduleRender();
  }

  getBlendMode(): BlendMode {
    return this.blendMode;
  }

  setBlendMode(mode: BlendMode): void {
    if (this.blendMode === mode) return;
    this.blendMode = mode;
    this.scheduleRender();
  }

  getFloorEnabled(): boolean {
    return this.floorEnabled;
  }

  setFloorEnabled(enabled: boolean): void {
    if (this.floorEnabled === enabled) return;
    this.floorEnabled = enabled;
    this.scheduleRender();
  }

  /** app.ts calls this whenever its per-station composite pollers update —
   * always, regardless of whether fleet.json is available, since it's
   * needed for loop/scrub frame history either way; only actually DRIVES a
   * fusion update when fleet.json is currently unavailable (fallback path). */
  onCompositeManifestsChanged(manifests: ReadonlyMap<string, HotManifest | null>): void {
    this.compositeManifests = manifests;
    if (!this.fleetAvailable) this.onStationsChanged();
  }

  goLive(): void {
    this.liveMode = true;
    this.onStationsChanged();
  }

  /** Shared scrub/loop entry point (same time axis as app.ts's
   * compositeScrubTimeMs) — per-station frame-at-or-before, mirroring
   * scrub.ts's existing per-station logic, just fanned out across every
   * station instead of one mosaic ts. */
  showAtOrBefore(ms: number): void {
    this.liveMode = false;
    this.lastScrubMs = ms;
    if (!this.active) return;
    const level = this.currentLevel();
    for (let idx = 0; idx < STATIONS.length; idx++) {
      const manifest = this.compositeManifests.get(STATIONS[idx].id);
      const frame = manifest ? frameAtOrBefore(manifest.frames, ms) : null;
      if (!frame) {
        this.clearStationSlot(idx);
        continue;
      }
      if (frame.ts !== this.slotTs[idx] || this.slotLevel[idx] !== level) void this.updateStationSlot(idx, frame.ts, level);
    }
    this.preloadAhead(ms, level);
  }

  /** Click/hover hit test for the fused canvas (there's no per-station
   * MapLibre layer to query anymore — same reasoning the old per-station
   * composite path had for needing a manual test instead of
   * queryRenderedFeatures, just against `slotBbox` instead of
   * compositeFrameBounds). Topmost = highest STATIONS index, arbitrary but
   * stable tie-break, matching the old draw-order convention. */
  stationAtLngLat(lng: number, lat: number): string | null {
    const [x, y] = lonLatToMercator(lng, lat);
    for (let idx = STATIONS.length - 1; idx >= 0; idx--) {
      const b = this.slotBbox[idx];
      if (b && x >= b.minX && x <= b.maxX && y >= b.minY && y <= b.maxY) return STATIONS[idx].id;
    }
    return null;
  }

  private currentStationsList(): FleetStation[] {
    if (this.fleetAvailable && this.fleetStations) return this.fleetStations;
    return STATIONS.map((s) => ({ id: s.id, lat: s.lat, lon: s.lon, latest: this.compositeManifests.get(s.id)?.latest ?? null }));
  }

  /** The single per-view overview-level decision (fusionGeo.ts's doc
   * comment): every call site below computes it exactly once, from the
   * current viewport zoom alone, and applies that ONE value to every
   * station slot it touches that pass — never a per-station level. */
  private currentLevel(): OverviewLevel {
    return overviewLevelForZoom(this.map.getZoom());
  }

  private isRelevant(station: FleetStation): boolean {
    const b = this.map.getBounds();
    return (
      station.lon >= b.getWest() - RELEVANCE_PAD_DEG &&
      station.lon <= b.getEast() + RELEVANCE_PAD_DEG &&
      station.lat >= b.getSouth() - RELEVANCE_PAD_DEG &&
      station.lat <= b.getNorth() + RELEVANCE_PAD_DEG
    );
  }

  private onStationsChanged(): void {
    this.onDataChanged();
    if (!this.active || !this.liveMode) return; // scrub mode is driven by showAtOrBefore(); hidden means don't spend bandwidth (same discipline as the old mosaic path)
    const level = this.currentLevel();
    for (const st of this.currentStationsList()) {
      const idx = STATIONS.findIndex((s) => s.id === st.id);
      if (idx < 0 || !st.latest || !this.isRelevant(st)) continue;
      if (st.latest.ts !== this.slotTs[idx] || this.slotLevel[idx] !== level) void this.updateStationSlot(idx, st.latest.ts, level);
    }
  }

  private onViewportSettled(): void {
    if (!this.active) return;
    const level = this.currentLevel();
    for (let idx = 0; idx < STATIONS.length; idx++) {
      if (this.slotTs[idx] && this.slotLevel[idx] !== level) void this.updateStationSlot(idx, this.slotTs[idx]!, level);
    }
    this.onStationsChanged(); // pick up newly-relevant stations at the new viewport
    this.scheduleRender(); // the window itself moved even if no station's data changed
  }

  private async updateStationSlot(idx: number, ts: string, level: OverviewLevel): Promise<void> {
    const stationId = STATIONS[idx].id;
    const key = `${stationId}:${ts}:${level}`;
    let result = this.cache.get(key);
    if (!result) {
      try {
        const url = frameUrl(this.getBaseUrl(), stationId, ts);
        result = await this.decoder.decode(stationId, url, level, key);
        this.cache.set(key, result);
        this.logBytes(key, result.bytesTransferred);
      } catch {
        return; // best-effort — leave whatever's currently loaded for this station
      }
    }
    this.slotTs[idx] = ts;
    this.slotLevel[idx] = level;
    this.slotBbox[idx] = result.bbox;
    for (const c of this.compositors) {
      c?.setStationTexture(idx, { raw: result.raw, width: result.width, height: result.height, bbox: result.bbox, center: this.stationCenters[idx] });
    }
    this.scheduleRender();
  }

  private clearStationSlot(idx: number): void {
    if (this.slotTs[idx] === null) return;
    this.slotTs[idx] = null;
    this.slotLevel[idx] = null;
    this.slotBbox[idx] = null;
    for (const c of this.compositors) c?.setStationTexture(idx, null);
    this.scheduleRender();
  }

  /** "preload the next 1-2 frames in loop mode": fire-and-forget decodes
   * for each station's own next couple of upcoming frames (per-station
   * cadence varies — workers/CONTRACTS.md/research: SAILS-heavy stations
   * post far more often than quiet ones — so this is genuinely per-station,
   * not one shared frame index like a single mosaic ts would have been). */
  private preloadAhead(fromMs: number, level: OverviewLevel): void {
    for (const s of STATIONS) {
      const manifest = this.compositeManifests.get(s.id);
      if (!manifest) continue;
      const startIdx = manifest.frames.findIndex((f) => f.dataTimeLastMs > fromMs);
      if (startIdx < 0) continue;
      for (let k = 0; k < PRELOAD_AHEAD; k++) {
        const f = manifest.frames[startIdx + k];
        if (!f) break;
        const key = `${s.id}:${f.ts}:${level}`;
        if (this.cache.has(key)) continue;
        const url = frameUrl(this.getBaseUrl(), s.id, f.ts);
        void this.decoder
          .decode(s.id, url, level, key)
          .then((r) => this.cache.set(key, r))
          .catch(() => {});
      }
    }
  }

  private scheduleRender(): void {
    if (this.renderScheduled || !this.fusionAvailable) return;
    this.renderScheduled = true;
    requestAnimationFrame(() => {
      this.renderScheduled = false;
      this.doRender();
    });
  }

  private outputSize(): { width: number; height: number } {
    const canvas = this.map.getCanvas();
    return { width: Math.min(canvas.width, MAX_OUTPUT_PX), height: Math.min(canvas.height, MAX_OUTPUT_PX) };
  }

  private doRender(): void {
    if (!this.layersReady) return;
    const t0 = performance.now();
    const bounds = this.map.getBounds();
    const window = lonLatBoundsToMercator({ west: bounds.getWest(), south: bounds.getSouth(), east: bounds.getEast(), north: bounds.getNorth() });

    const stationMeta = STATIONS.map((_, idx) => {
      const b = this.slotBbox[idx];
      if (!b || !boundsIntersect(window, b)) return null;
      const uv = stationUvTransform(window, b);
      return uv ? { uv, center: this.stationCenters[idx] } : null;
    });

    const nextSlot: 0 | 1 = this.activeSlot === 0 ? 1 : 0;
    const compositor = this.compositors[nextSlot];
    if (!compositor) return;
    const { width, height } = this.outputSize();
    const floorRaw = this.floorEnabled ? PRECIP_FLOOR_RAW : NO_FLOOR_RAW;
    compositor.render(window, this.blendMode, floorRaw, width, height, stationMeta);

    const sourceId = SOURCE_IDS[nextSlot];
    const layerId = layerIdFor(sourceId);
    const prevLayerId = layerIdFor(SOURCE_IDS[this.activeSlot]);
    const source = this.map.getSource(sourceId) as maplibregl.ImageSource | undefined;
    const coordinates: [[number, number], [number, number], [number, number], [number, number]] = [
      [bounds.getWest(), bounds.getNorth()],
      [bounds.getEast(), bounds.getNorth()],
      [bounds.getEast(), bounds.getSouth()],
      [bounds.getWest(), bounds.getSouth()],
    ];
    source?.updateImage({ image: compositor.canvas, coordinates });

    if (this.active) {
      this.map.setPaintProperty(layerId, "raster-opacity", FUSION_OPACITY);
      this.map.setPaintProperty(prevLayerId, "raster-opacity", 0);
    }
    this.activeSlot = nextSlot;
    this.logRender(performance.now() - t0);
  }

  private logRender(ms: number): void {
    if (!DEV) return;
    if (!this.firstPaintLogged) {
      this.firstPaintLogged = true;
      performance.mark("radar:fusion-first-paint");
      try {
        const measure = performance.measure("radar:cold-load", "radar:app-init", "radar:fusion-first-paint");
        // eslint-disable-next-line no-console
        console.debug(`[fusion] cold-load (app init -> first fused paint): ${measure.duration.toFixed(1)}ms`);
      } catch {
        /* radar:app-init mark missing (e.g. hot-reload) — skip the cold-load number */
      }
    }
    // eslint-disable-next-line no-console
    console.debug(`[fusion] composite redraw (${this.blendMode}): ${ms.toFixed(1)}ms`);
  }

  private logBytes(key: string, bytes: number): void {
    if (!DEV) return;
    // eslint-disable-next-line no-console
    console.debug(`[fusion] decoded ${key}: ${(bytes / 1024).toFixed(1)} KiB over the wire`);
  }
}
